import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase/config';
import { theme } from '../styles/GlobalStyles';
import Input from '../components/Input';
import Button from '../components/Button';
import { TopWaves, BottomWaves } from '../components/WaveShapes';
import { ArrowLeftIcon } from '../components/Icons';

const LoginContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: ${theme.spacing.lg};
  background-color: ${theme.colors.white};
  position: relative;
  overflow: hidden;
`;

const LoginCard = styled.div`
  background-color: ${theme.colors.white};
  border-radius: 24px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  padding: ${theme.spacing.xl};
  width: 100%;
  max-width: 400px;
  z-index: 10;
  position: relative;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
    padding: ${theme.spacing.lg} ${theme.spacing.md};
    border-radius: 16px;
    margin: ${theme.spacing.md};
    max-width: calc(100% - ${theme.spacing.xl});
  }
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #7f8c8d;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  font-size: 0.9rem;
  margin-bottom: ${theme.spacing.md};
  padding: ${theme.spacing.xs};
  transition: all 0.2s ease;
  
  &:hover {
    color: #2c3e50;
  }
`;

const LoginTitle = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing.lg};
`;

const LogoImage = styled.img`
  height: auto;
  max-width: 220px;
  margin: 0 auto;
  display: block;
`;

const WelcomeText = styled.h2`
  color: ${theme.colors.deepDark};
  margin: ${theme.spacing.md} 0;
  font-size: 1.8rem;
  font-weight: 600;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.5rem;
    margin: ${theme.spacing.sm} 0;
  }
`;

const SubTitle = styled.p`
  color: ${theme.colors.lightText};
  font-size: 1rem;
  margin-bottom: ${theme.spacing.md};
`;

const LoginForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const LoginFooter = styled.div`
  margin-top: ${theme.spacing.lg};
  text-align: center;
  font-size: 0.875rem;
  color: ${theme.colors.lightText};
  
  a {
    color: ${theme.colors.primary};
    font-weight: 500;
    text-decoration: none;
    margin-left: 5px;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const ErrorMessage = styled.div`
  background-color: rgba(228, 139, 136, 0.1);
  border: 1px solid ${theme.colors.error};
  border-radius: 12px;
  color: ${theme.colors.error};
  padding: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.md};
  font-size: 0.875rem;
  role: "alert";
`;

const SignInButton = styled(Button)`
  margin-top: ${theme.spacing.sm};
  height: 48px;
  background: linear-gradient(to right, ${theme.colors.primary}, ${theme.colors.tertiary});
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    height: ${theme.mobile.minTouchTarget};
    font-size: 1rem;
    margin-top: ${theme.spacing.md};
  }
`;

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();
  
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= parseInt(theme.breakpoints.mobile));
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }
    
    try {
      setError('');
      setLoading(true);
      await signInWithEmailAndPassword(auth, email, password);
      navigate('/app');
    } catch (err: any) {
      console.error("Login error:", err);
      setError(err.message || 'Failed to sign in. Please check your credentials and try again.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <LoginContainer>
      {!isMobile && <TopWaves />}
      {!isMobile && <BottomWaves />}
      
      <LoginCard>
        <BackButton onClick={() => navigate('/')}>
          <ArrowLeftIcon size={20} />
        </BackButton>
        
        <LoginTitle>
          <LogoImage src="/images/MMNHeader.png" alt="My NeuroNest" />
          <WelcomeText>Hello</WelcomeText>
          <SubTitle>Sign in to your account</SubTitle>
        </LoginTitle>
        
        {error && <ErrorMessage role="alert">{error}</ErrorMessage>}
        
        <LoginForm onSubmit={handleLogin} aria-label="Login form" noValidate>
          <Input
            id="email"
            name="email"
            label="Email"
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            fullWidth
            autoComplete="email"
          />
          
          <Input
            id="password"
            name="password"
            label="Password"
            type="password"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            fullWidth
            autoComplete="current-password"
          />
          
          <SignInButton 
            type="submit" 
            fullWidth 
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign in'}
          </SignInButton>
        </LoginForm>
        
        <LoginFooter>
          Don't have an account?<Link to="/signup">Create account</Link>
        </LoginFooter>
      </LoginCard>
    </LoginContainer>
  );
};

export default Login; 